/**
 * CommentList Component
 *
 * Story BL-024.2: Governance-Suite Comment Thread UI
 * Task 5: Create CommentList component
 *
 * Renders a list of comment threads with loading skeleton and empty state.
 *
 * @module components/governance/CommentList
 */

import type { CommentThread } from '../../types/proposalComments';
import { CommentItem } from './CommentItem';

// =============================================================================
// Types
// =============================================================================

export interface CommentListProps {
  /** Array of top-level comments with their replies */
  comments: CommentThread[];
  /** Whether comments are currently loading */
  isLoading: boolean;
  /** Current logged-in user ID */
  currentUserId: string | undefined;
  /** Whether the current user is authenticated */
  isAuthenticated: boolean;
  /** Whether the current user is admin or moderator */
  isModeratorOrAdmin: boolean;
  /** Vote callback */
  onVote: (commentId: string, vote: 1 | -1) => void;
  /** Reply callback */
  onReply: (commentId: string, content: string) => Promise<void>;
  /** Delete callback */
  onDelete: (commentId: string) => void;
  /** Edit callback */
  onEdit: (commentId: string, content: string) => Promise<void>;
  /** Custom empty state message (BL-024.3 AC7) */
  emptyMessage?: string;
}

// =============================================================================
// Component
// =============================================================================

export function CommentList({
  comments,
  isLoading,
  currentUserId,
  isAuthenticated,
  isModeratorOrAdmin,
  onVote,
  onReply,
  onDelete,
  onEdit,
  emptyMessage,
}: CommentListProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="comments-loading-skeleton">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="animate-pulse h-16 bg-muted rounded-lg"
            data-testid="skeleton-row"
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (comments.length === 0) {
    return (
      <div
        className="text-center py-8 text-muted-foreground"
        data-testid="comments-empty-state"
      >
        {emptyMessage ?? 'Be the first to comment on this proposal.'}
      </div>
    );
  }

  // Comment list
  return (
    <div className="divide-y divide-border" data-testid="comments-list">
      {comments.map((thread) => (
        <CommentItem
          key={thread.id}
          comment={thread}
          currentUserId={currentUserId}
          isAuthenticated={isAuthenticated}
          isModeratorOrAdmin={isModeratorOrAdmin}
          onVote={onVote}
          onReply={onReply}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}

export default CommentList;
